// Seletor de período da tela de Desembolso: o mesmo `filterDates` do contexto que a
// Projeção Financeira usa (`changeDates` + a chave `<obra>_filterDates` no storage), para
// que o período escolhido numa tela continue valendo na outra em vez de duas verdades
// sobre "o período da obra".
//
// É a versão enxuta do `DateRangeFilter`: sem os botões de relatório/exportação, que
// pertencem à Projeção. Mesma marcação (`form.date-range` + dois campos de competência),
// que é o padrão de layout da Ampli para escolher período — com o `CompetenceInput` do
// módulo no lugar do `input[type=month]`, para o período sair em MM/AAAA em qualquer idioma
// do navegador (design D8).
import { useCallback } from "react";

import { useProjectActions, useProjectContext } from "@contexts/projects";
import { ensureDateType } from "@utils/formatDate";
import { saveStorage } from "@utils/localStorageManager";

import CompetenceInput from "./CompetenceInput";

const toInput = (value) => ensureDateType(value).toISOString().slice(0, 7);

const fromInput = (value) => {
  const [year, month] = value.split("-");
  return new Date(Date.UTC(Number(year), Number(month) - 1, 1));
};

const DisbursementPeriodFilter = () => {
  const { activeProject = {}, filterDates } = useProjectContext();
  const { changeDates } = useProjectActions();
  const {
    _id: projectId,
    start_date: startDate,
    end_date: endDate,
  } = activeProject;

  const apply = useCallback(
    (beginAt, endAt) => {
      saveStorage(`${projectId}_filterDates`, { beginAt, endAt });
      changeDates(projectId, beginAt, endAt);
    },
    [changeDates, projectId],
  );

  const changeStart = useCallback(
    (value) => {
      if (!value) return;
      apply(fromInput(value), filterDates.endAt);
    },
    [apply, filterDates?.endAt],
  );

  const changeEnd = useCallback(
    (value) => {
      if (!value) return;
      apply(filterDates.beginAt, fromInput(value));
    },
    [apply, filterDates?.beginAt],
  );

  // Sem obra, sem prazo ou sem período resolvido não há o que escolher — e um campo
  // com `value` inválido viraria um campo vazio.
  if (
    !projectId ||
    !startDate ||
    !endDate ||
    !filterDates?.beginAt ||
    !filterDates?.endAt
  ) {
    return null;
  }

  return (
    <form className="date-range" onSubmit={(event) => event.preventDefault()}>
      <h3>Período:</h3>
      <CompetenceInput
        className=""
        aria-label="Início do período do desembolso"
        min={toInput(startDate)}
        max={toInput(filterDates.endAt)}
        value={toInput(filterDates.beginAt)}
        onChange={changeStart}
      />
      <span className="color-gray">-</span>
      <CompetenceInput
        className=""
        aria-label="Fim do período do desembolso"
        min={toInput(filterDates.beginAt)}
        max={toInput(endDate)}
        value={toInput(filterDates.endAt)}
        onChange={changeEnd}
      />
    </form>
  );
};

export default DisbursementPeriodFilter;
